// ===================================================================
// KAIXO - TranslateCameraModal
// ===================================================================
// Modal aislado: cámara live → foto → OCR + traducción Claude Vision.
// Flujo:
//   1. Pantalla cámara (video + botón disparador + cerrar)
//   2. Pantalla "procesando" (spinner)
//   3. Pantalla resultado (foto pequeña + texto original + traducción
//      + acciones: escuchar, copiar, otra foto, cerrar, opcional enviar
//      al chat si onSendToChat se pasó)
//
// Props:
//   - userLanguage: idioma destino (string KAIXO, ej. "español")
//   - onClose: cerrar modal
//   - onSendToChat?: (text) => void  — opcional, muestra botón "Enviar al chat"
// ===================================================================

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import t from '../../i18n/useTranslation';
import { translatePhoto, translatePhotoStream, parsePhotoStreamBuffer } from '../../services/photoTranslation.service';
import { KAIXO_LANGUAGES, langToBCP47 } from '../../utils/langCode';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { Capacitor } from '@capacitor/core';
import './TranslateCameraModal.css';

const STAGE_CAMERA = 'camera';
const STAGE_PROCESSING = 'processing';
const STAGE_RESULT = 'result';
const STAGE_ERROR = 'error';

export default function TranslateCameraModal({ userLanguage, onClose: onCloseProp, onSendToChat }) {
    // Wrapper de onClose: SIEMPRE para el TTS antes de cerrar — el cleanup
    // del useEffect también lo hace, pero al pulsar el botón cerrar
    // queremos que el sonido pare AL INSTANTE, no en el siguiente tick.
    const onClose = () => {
        try {
            if (typeof window !== 'undefined' && window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        } catch {}
        try {
            if (Capacitor.isNativePlatform?.()) {
                TextToSpeech.stop().catch(() => {});
            }
        } catch {}
        onCloseProp?.();
    };
    const [stage, setStage] = useState(STAGE_CAMERA);
    const [errorMsg, setErrorMsg] = useState('');
    const [result, setResult] = useState(null); // { original, translation, hasText, sourceLanguage, photoUrl }
    const [photoUrl, setPhotoUrl] = useState(null);
    // Idioma destino de la traducción. Default: el del propio user. Se
    // puede cambiar desde un desplegable encima de la cámara para casos
    // como "le enseño esto a alguien que habla otro idioma".
    const [targetLanguage, setTargetLanguage] = useState(userLanguage || 'español');
    const [showLangPicker, setShowLangPicker] = useState(false);

    // Zoom: cámara muestra demasiado cerca por defecto. Permitimos pills
    // 0.5×/1×/2×/3×. 0.5× usa la cámara ultra-wide física si el dispositivo
    // la tiene; 2×/3× usa applyConstraints({ zoom }) del track si soporta.
    const [zoomLevel, setZoomLevel] = useState(1);
    const [hasUltraWide, setHasUltraWide] = useState(false);
    const [maxZoom, setMaxZoom] = useState(1); // máx que el track regular permite
    const backCamerasRef = useRef([]); // [regularDeviceId, ultraWideDeviceId?]
    const [ttsErr, setTtsErr] = useState('');
    const [isSpeaking, setIsSpeaking] = useState(false);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const ttsRef = useRef(null);

    // Detiene el stream activo, si hay
    const stopCurrentStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(tr => tr.stop());
            streamRef.current = null;
        }
    };

    // Pequeña pausa para que el hardware libere la cámara entre stops y starts
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    // Intenta abrir un stream con un set de constraints. Si el deviceId
    // exact falla (cámara no disponible/ocupada), intenta con facingMode
    // como red de seguridad. Devuelve null si todo falla.
    const tryGetStream = async (constraints) => {
        try {
            return await navigator.mediaDevices.getUserMedia({ video: constraints, audio: false });
        } catch (e) {
            // Fallback a facingMode si el constraint era con deviceId
            if (constraints?.deviceId) {
                try {
                    return await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
                        audio: false
                    });
                } catch {}
            }
            return null;
        }
    };

    // Arranca cámara con un nivel de zoom dado. Si zoom===0.5 intenta usar
    // la cámara ultra-wide física; si falla cae a la regular en zoom 1
    // sin romper la pantalla y sin marcar error al usuario.
    const startCamera = async (level = 1, cancelledRef = { cancelled: false }) => {
        try {
            // Damos 80ms para que el hardware libere recursos del stream
            // anterior (necesario en algunos Android al conmutar cámara).
            await sleep(80);
            if (cancelledRef.cancelled) return;

            const useUltraWide = level === 0.5 && backCamerasRef.current[1];
            const targetDeviceId = useUltraWide ? backCamerasRef.current[1] : backCamerasRef.current[0];

            const baseConstraints = targetDeviceId
                ? { deviceId: { exact: targetDeviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
                : { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } };

            let stream = await tryGetStream(baseConstraints);

            // Si pedimos ultra-wide y no se pudo (con o sin fallback a
            // facingMode), volvemos a 1× sin romper UI.
            if (!stream && useUltraWide) {
                if (cancelledRef.cancelled) return;
                stream = await tryGetStream(
                    backCamerasRef.current[0]
                        ? { deviceId: { exact: backCamerasRef.current[0] }, width: { ideal: 1920 }, height: { ideal: 1080 } }
                        : { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
                );
                if (stream) {
                    setZoomLevel(1);
                    setTtsErr(''); // por si había uno previo
                    setErrorMsg('');
                }
            }

            if (!stream) {
                if (!cancelledRef.cancelled) {
                    setErrorMsg(t.chat.photoTranslate?.errCamera || 'No se pudo acceder a la cámara');
                    setStage(STAGE_ERROR);
                }
                return;
            }

            if (cancelledRef.cancelled) {
                stream.getTracks().forEach(tr => tr.stop());
                return;
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }

            // Primera vez: enumerar cámaras traseras y separar regular vs
            // ultra-wide. Detección ESTRICTA: solo marcamos hasUltraWide
            // si el label menciona explícitamente ultra/wide-angle/0.5.
            // Sin esto, en algunos móviles el segundo deviceId era una
            // cámara telephoto (zoom-in) y al pulsar 0.5× pedíamos un
            // dispositivo que no era ultra-wide → fallaba con "no se
            // puede acceder a la cámara".
            if (backCamerasRef.current.length === 0) {
                try {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const backs = devices
                        .filter(d => d.kind === 'videoinput')
                        .filter(d => {
                            const lbl = (d.label || '').toLowerCase();
                            return !/front|face|user|frontal/.test(lbl);
                        });
                    const explicit = backs.find(d => /ultra|wide.?angle|0\.5/i.test(d.label || ''));
                    const isMobileNative = Capacitor.isNativePlatform?.() === true;
                    if (explicit) {
                        const regular = backs.find(d => d.deviceId !== explicit.deviceId) || backs[0];
                        backCamerasRef.current = [regular?.deviceId, explicit.deviceId].filter(Boolean);
                        setHasUltraWide(backCamerasRef.current.length > 1);
                    } else if (isMobileNative && backs.length >= 2) {
                        // En móvil nativo con 2+ cámaras traseras y sin match
                        // explícito por label (algunos dispositivos no
                        // exponen labels descriptivos), asumimos que la 2ª
                        // puede ser ultra-wide. Si al pulsar 0.5× falla,
                        // tryGetStream cae a 1× sin romper la UI.
                        backCamerasRef.current = backs.slice(0, 2).map(d => d.deviceId);
                        setHasUltraWide(true);
                    } else {
                        backCamerasRef.current = backs.slice(0, 2).map(d => d.deviceId);
                        // Web/PC: sin match explícito, no mostramos 0.5×.
                        setHasUltraWide(false);
                    }
                } catch {}
            }

            // Detectar capacidad de zoom del track activo
            const track = stream.getVideoTracks()[0];
            let trackMaxZoom = 1;
            if (track?.getCapabilities) {
                try {
                    const caps = track.getCapabilities();
                    if (caps?.zoom?.max) trackMaxZoom = caps.zoom.max;
                } catch {}
            }
            setMaxZoom(trackMaxZoom);

            // Aplicar zoom digital si pedimos 2× o 3× sobre la regular
            if (!useUltraWide && level > 1 && track?.getCapabilities) {
                try {
                    const caps = track.getCapabilities();
                    if (caps?.zoom) {
                        const target = Math.min(level, caps.zoom.max || 1);
                        await track.applyConstraints({ advanced: [{ zoom: target }] }).catch(() => {});
                    }
                } catch {}
            }
        } catch (e) {
            if (cancelledRef.cancelled) return;
            setErrorMsg(t.chat.photoTranslate?.errCamera || 'No se pudo acceder a la cámara');
            setStage(STAGE_ERROR);
        }
    };

    // Iniciar cámara al entrar al stage CAMERA. Re-ejecuta si cambia el
    // zoom para conmutar la cámara física (0.5× ↔ regular).
    useEffect(() => {
        if (stage !== STAGE_CAMERA) return;
        const cancelledRef = { cancelled: false };
        stopCurrentStream();
        startCamera(zoomLevel, cancelledRef);
        return () => {
            cancelledRef.cancelled = true;
            stopCurrentStream();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, zoomLevel]);

    // Cleanup global al desmontar — para de hablar (TTS), libera cámara,
    // libera URL de la foto. CRÍTICO: stopTts antes de salir, si no la
    // voz seguía sonando aunque cierres el modal.
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(tr => tr.stop());
                streamRef.current = null;
            }
            if (photoUrl) URL.revokeObjectURL(photoUrl);
            try {
                if (typeof window !== 'undefined' && window.speechSynthesis) {
                    window.speechSynthesis.cancel();
                }
            } catch {}
            try {
                if (Capacitor.isNativePlatform?.()) {
                    TextToSpeech.stop().catch(() => {});
                }
            } catch {}
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleShutter = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);

        canvas.toBlob(async (blob) => {
            if (!blob) return;
            stopCurrentStream();
            const url = URL.createObjectURL(blob);
            setPhotoUrl(url);
            setResult({ hasText: true, sourceLanguage: '', original: '', translation: '' });
            setStage(STAGE_RESULT);

            const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
            let acc = '';
            let firstDeltaArrived = false;
            let fallbackFired = false;
            const ac = new AbortController();

            // Si tras 5s el streaming NO ha producido nada (típicamente
            // por buffering del proxy de Render), abortamos y caemos al
            // endpoint normal — entrega la respuesta de golpe pero al
            // menos el usuario ve la traducción.
            const fallbackTimer = setTimeout(async () => {
                if (firstDeltaArrived || fallbackFired) return;
                fallbackFired = true;
                try { ac.abort(); } catch {}
                try {
                    const data = await translatePhoto(file, targetLanguage || 'español');
                    setResult({
                        hasText: !!data?.hasText,
                        sourceLanguage: data?.sourceLanguage === 'unknown' ? '' : (data?.sourceLanguage || ''),
                        original: data?.original || '',
                        translation: data?.translation || ''
                    });
                } catch (err) {
                    setErrorMsg(err?.response?.data?.error || err?.message || (t.chat.photoTranslate?.errProcess || 'Error al procesar la foto'));
                    setStage(STAGE_ERROR);
                }
            }, 5000);

            await translatePhotoStream(file, targetLanguage || 'español', {
                signal: ac.signal,
                onDelta: (chunk) => {
                    if (fallbackFired) return;
                    if (!firstDeltaArrived) {
                        firstDeltaArrived = true;
                        clearTimeout(fallbackTimer);
                    }
                    acc += chunk;
                    const parsed = parsePhotoStreamBuffer(acc);
                    setResult({
                        hasText: !!(parsed.original || parsed.translation) || !parsed.done,
                        sourceLanguage: parsed.sourceLanguage,
                        original: parsed.original,
                        translation: parsed.translation
                    });
                },
                onDone: () => {
                    if (fallbackFired) return;
                    clearTimeout(fallbackTimer);
                    const parsed = parsePhotoStreamBuffer(acc);
                    const finalHasText = !(parsed.sourceLanguage === 'none')
                        && (parsed.original || parsed.translation);
                    setResult({
                        hasText: !!finalHasText,
                        sourceLanguage: parsed.sourceLanguage === 'none' ? '' : parsed.sourceLanguage,
                        original: parsed.original,
                        translation: parsed.translation
                    });
                },
                onError: (err) => {
                    if (fallbackFired) return; // ya estamos en fallback
                    clearTimeout(fallbackTimer);
                    // Si el error es por abort por nuestro propio timer, ignoramos
                    if (err?.name === 'AbortError' || /aborted/i.test(err?.message || '')) return;
                    setErrorMsg(err?.message || (t.chat.photoTranslate?.errProcess || 'Error al procesar la foto'));
                    setStage(STAGE_ERROR);
                }
            });
        }, 'image/jpeg', 0.85);
    };

    const handleAnother = () => {
        stopTts();
        if (photoUrl) URL.revokeObjectURL(photoUrl);
        setPhotoUrl(null);
        setResult(null);
        setErrorMsg('');
        setTtsErr('');
        setStage(STAGE_CAMERA);
    };

    // Detiene cualquier TTS activo (web + Capacitor) y limpia estado.
    // Centralizado en una sola función para usarlo desde stop button,
    // unmount, "otra foto", cambio de idioma, etc.
    const stopTts = () => {
        try {
            if (typeof window !== 'undefined' && window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        } catch {}
        try {
            if (Capacitor.isNativePlatform?.()) {
                TextToSpeech.stop().catch(() => {});
            }
        } catch {}
        setIsSpeaking(false);
    };

    const handleSpeak = async (text) => {
        // Si ya está hablando, este botón actúa como STOP.
        if (isSpeaking) {
            stopTts();
            return;
        }
        if (!text) return;
        const bcp = langToBCP47(targetLanguage);
        setTtsErr('');
        setIsSpeaking(true);

        const hasWebTts = typeof window !== 'undefined'
            && typeof window.speechSynthesis !== 'undefined';

        if (hasWebTts) {
            try {
                window.speechSynthesis.cancel();
                const voices = window.speechSynthesis.getVoices() || [];
                const langPrefix = bcp.split('-')[0].toLowerCase();
                const voiceExact = voices.find(v => (v.lang || '').toLowerCase() === bcp.toLowerCase());
                const voicePrefix = voices.find(v => (v.lang || '').toLowerCase().startsWith(langPrefix));
                const u = new SpeechSynthesisUtterance(text);
                u.lang = bcp;
                if (voiceExact) u.voice = voiceExact;
                else if (voicePrefix) u.voice = voicePrefix;
                u.rate = 1.0;
                let webFailed = false;
                let webStarted = false;
                u.onstart = () => { webStarted = true; };
                u.onend = () => { setIsSpeaking(false); };
                u.onerror = (ev) => {
                    if (ev?.error && ev.error !== 'interrupted' && ev.error !== 'canceled') {
                        webFailed = true;
                        if (Capacitor.isNativePlatform?.()) {
                            TextToSpeech.speak({
                                text, lang: bcp, rate: 1.0, pitch: 1.0, volume: 1.0, category: 'playback'
                            }).then(() => setIsSpeaking(false))
                              .catch(err => { setTtsErr(err?.message || 'TTS error'); setIsSpeaking(false); });
                        } else {
                            setTtsErr(`${t.chat.photoTranslate?.ttsError || 'TTS error'}: ${ev.error}`);
                            setIsSpeaking(false);
                        }
                    } else {
                        setIsSpeaking(false);
                    }
                };
                ttsRef.current = u;
                window.speechSynthesis.speak(u);
                if (Capacitor.isNativePlatform?.()) {
                    setTimeout(() => {
                        try {
                            const speaking = window.speechSynthesis.speaking || window.speechSynthesis.pending;
                            if (!speaking && !webStarted && !webFailed) {
                                TextToSpeech.speak({
                                    text, lang: bcp, rate: 1.0, pitch: 1.0, volume: 1.0, category: 'playback'
                                }).then(() => setIsSpeaking(false))
                                  .catch(err => { setTtsErr(err?.message || 'TTS error'); setIsSpeaking(false); });
                            }
                        } catch {}
                    }, 600);
                }
                return;
            } catch (e) {
                console.warn('[tcm tts web]', e?.message);
                setIsSpeaking(false);
            }
        }

        if (Capacitor.isNativePlatform?.()) {
            try {
                try { await TextToSpeech.stop(); } catch {}
                await TextToSpeech.speak({
                    text, lang: bcp, rate: 1.0, pitch: 1.0, volume: 1.0, category: 'playback'
                });
                setIsSpeaking(false);
            } catch (e) {
                setTtsErr(e?.message || 'TTS error');
                setIsSpeaking(false);
            }
        } else {
            setTtsErr(t.chat.photoTranslate?.ttsUnavailable || 'TTS no disponible');
            setIsSpeaking(false);
        }
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {}
    };

    const handleSendToChatClick = () => {
        if (!result?.translation) return;
        // Enviamos SOLO la traducción (no concatenamos el original) y le
        // pasamos targetLanguage como originalLanguage del mensaje, así:
        //   - el remitente ve su propio mensaje en el idioma destino
        //   - los receptores reciben la auto-traducción del chat a SU idioma
        //   - el TTS escoge la voz correcta del idioma destino
        // Antes mandábamos original+traducción juntos, lo que se veía como
        // dos bloques sin diferenciar y daba la sensación de duplicación.
        onSendToChat?.({
            text: result.translation,
            language: targetLanguage
        });
        onClose?.();
    };

    return ReactDOM.createPortal(
        <div className="tcm-overlay">
            <button className="tcm-close" onClick={onClose} aria-label="Close">
                <Icons.Close size={22} color="white" />
            </button>

            {stage === STAGE_CAMERA && (
                <>
                    <video
                        ref={videoRef}
                        className="tcm-video"
                        playsInline
                        muted
                        autoPlay
                        controls={false}
                        disablePictureInPicture
                    />
                    <div className="tcm-lang-bar">
                        <button
                            type="button"
                            className="tcm-lang-button"
                            onClick={() => setShowLangPicker(v => !v)}
                            aria-haspopup="listbox"
                            aria-expanded={showLangPicker}
                        >
                            <span className="tcm-lang-icon">🌐</span>
                            <span className="tcm-lang-current">
                                {(KAIXO_LANGUAGES.find(l => l.key === targetLanguage)?.label) || targetLanguage}
                            </span>
                            <span className="tcm-lang-arrow">{showLangPicker ? '▲' : '▼'}</span>
                        </button>
                        {showLangPicker && (
                            <div className="tcm-lang-picker" role="listbox">
                                {KAIXO_LANGUAGES.map(l => (
                                    <button
                                        key={l.key}
                                        type="button"
                                        role="option"
                                        aria-selected={l.key === targetLanguage}
                                        className={`tcm-lang-item ${l.key === targetLanguage ? 'selected' : ''}`}
                                        onClick={() => { setTargetLanguage(l.key); setShowLangPicker(false); }}
                                    >
                                        <span className="tcm-lang-item-label">{l.label}</span>
                                        {l.key === targetLanguage && <span className="tcm-lang-item-check">✓</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="tcm-hint">
                        {t.chat.photoTranslate?.hint || 'Apunta a un cartel o menú y dispara'}
                    </div>

                    {/* Pills de zoom — solo se muestra el chip si el dispositivo
                        lo soporta (0.5× requiere ultra-wide; 2×/3× requieren
                        track.zoom). Con el chip activo, applyConstraints fija
                        el zoom o conmuta a la otra cámara física. */}
                    <div className="tcm-zoom-bar">
                        {/* 0.5× solo en móvil nativo — la webcam de PC NUNCA
                            es ultra-wide, así que aparecía y al pulsarla
                            fallaba con 'no se puede acceder a la cámara'. */}
                        {hasUltraWide && Capacitor.isNativePlatform?.() && (
                            <button
                                type="button"
                                className={`tcm-zoom-chip ${zoomLevel === 0.5 ? 'active' : ''}`}
                                onClick={() => setZoomLevel(0.5)}
                                aria-label="0.5x"
                            >0.5×</button>
                        )}
                        <button
                            type="button"
                            className={`tcm-zoom-chip ${zoomLevel === 1 ? 'active' : ''}`}
                            onClick={() => setZoomLevel(1)}
                            aria-label="1x"
                        >1×</button>
                        {maxZoom >= 2 && (
                            <button
                                type="button"
                                className={`tcm-zoom-chip ${zoomLevel === 2 ? 'active' : ''}`}
                                onClick={() => setZoomLevel(2)}
                                aria-label="2x"
                            >2×</button>
                        )}
                        {maxZoom >= 3 && (
                            <button
                                type="button"
                                className={`tcm-zoom-chip ${zoomLevel === 3 ? 'active' : ''}`}
                                onClick={() => setZoomLevel(3)}
                                aria-label="3x"
                            >3×</button>
                        )}
                    </div>

                    <button className="tcm-shutter" onClick={handleShutter} aria-label="Capture" />
                </>
            )}

            {stage === STAGE_PROCESSING && (
                <div className="tcm-stage tcm-processing">
                    {photoUrl && <img src={photoUrl} alt="" className="tcm-preview" />}
                    <div className="tcm-spinner" />
                    <div className="tcm-processing-text">
                        {t.chat.photoTranslate?.processing || 'Leyendo y traduciendo…'}
                    </div>
                </div>
            )}

            {stage === STAGE_RESULT && result && (
                <div className="tcm-stage tcm-result">
                    {photoUrl && <img src={photoUrl} alt="" className="tcm-preview" />}

                    {!result.original && !result.translation && (
                        <div className="tcm-streaming-row">
                            <div className="tcm-spinner-sm" />
                            <span>{t.chat.photoTranslate?.processing || 'Leyendo y traduciendo…'}</span>
                        </div>
                    )}

                    {!result.original && !result.translation && result.hasText === false && (
                        <div className="tcm-empty">
                            {t.chat.photoTranslate?.noText || 'No se detectó texto en la imagen.'}
                        </div>
                    )}

                    {result.original && (
                        <div className="tcm-block">
                            <div className="tcm-block-label">
                                {t.chat.photoTranslate?.original || 'Texto detectado'}
                                {result.sourceLanguage && result.sourceLanguage !== 'unknown' && (
                                    <span className="tcm-block-lang"> · {result.sourceLanguage}</span>
                                )}
                            </div>
                            <div className="tcm-block-value">{result.original}</div>
                        </div>
                    )}

                    {result.translation && (
                        <div className="tcm-block tcm-block-target">
                            <div className="tcm-block-label">
                                {t.chat.photoTranslate?.translation || 'En tu idioma'}
                            </div>
                            <div className="tcm-block-value">{result.translation}</div>
                        </div>
                    )}

                    {result.original && !result.translation && (
                        <div className="tcm-streaming-row tcm-streaming-row-inline">
                            <div className="tcm-spinner-sm" />
                            <span>{t.chat.photoTranslate?.processing || 'Traduciendo…'}</span>
                        </div>
                    )}

                    {ttsErr && (
                        <div className="tcm-tts-err">⚠️ {ttsErr}</div>
                    )}

                    <div className="tcm-actions">
                        {result.translation && (
                            <button
                                className={`tcm-btn ${isSpeaking ? 'tcm-btn-stop' : ''}`}
                                onClick={() => handleSpeak(result.translation)}
                            >
                                {isSpeaking
                                    ? <>⏹ {t.chat.photoTranslate?.stop || 'Detener'}</>
                                    : <>🔊 {t.chat.photoTranslate?.listen || 'Escuchar'}</>
                                }
                            </button>
                        )}
                        {result.translation && (
                            <button className="tcm-btn" onClick={() => handleCopy(result.translation)}>
                                📋 {t.chat.photoTranslate?.copy || 'Copiar'}
                            </button>
                        )}
                        {onSendToChat && result.translation && (
                            <button className="tcm-btn tcm-btn-primary" onClick={handleSendToChatClick}>
                                💬 {t.chat.photoTranslate?.sendToChat || 'Enviar al chat'}
                            </button>
                        )}
                        <button className="tcm-btn" onClick={handleAnother}>
                            📷 {t.chat.photoTranslate?.another || 'Otra foto'}
                        </button>
                    </div>
                </div>
            )}

            {stage === STAGE_ERROR && (
                <div className="tcm-stage tcm-error">
                    <div className="tcm-error-icon">!</div>
                    <div className="tcm-error-text">{errorMsg}</div>
                    <button className="tcm-btn" onClick={handleAnother}>
                        {t.chat.photoTranslate?.retry || 'Reintentar'}
                    </button>
                    <button className="tcm-btn" onClick={onClose}>
                        {t.chat.photoTranslate?.close || 'Cerrar'}
                    </button>
                </div>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>,
        document.body
    );
}
